/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : IntersectionIndex.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Grid } from "@mui/material";
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import CommonBreadcrumbs from "../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import ProjectVariantDialog from "../../../components/customComponentsV2/vlc/ProjectVarientAccordian/ProjectVariantDialog";
import ProjectvariantSearchbar from "../../../components/customComponentsV2/common/projectSearchbar/ProjectvariantSearchbar";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import TicketsTable from "./TicketsTable";
import { TICKETS_TYPES } from "../../../config/appConstants";
import { useDispatch, useSelector } from "react-redux";
import { HandleApiActions, projectConfigAPI } from "../../../redux/actions";
import {
  selectProjectAction,
  selectVariantAction,
} from "../../../redux/actions/projectConfigActions";
import typeApprovalAPI from "../../../redux/actions/typeApprovalAPI";
import moment from "moment/moment";
import { useSearchParams } from "react-router-dom";
import CustomTabsV2 from "../../../components/customComponentsV2/common/tabs/CustomTabsV2";
import TicketsFilter from "../tickets/TicketsFilter";

function IntersectionIndex() {
  const dispatch = useDispatch();
  const hydratedSelectionRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = Number.parseInt(searchParams.get("tab"), 10) || 0;
  const intrIdFromUrl = searchParams.get("intr_id");
  const projectIdFromUrl = searchParams.get("project_id");
  const variantIdFromUrl = searchParams.get("variant_id");
  const ticketTypes = [
    TICKETS_TYPES.VEHICLE_SYSTEM,
    TICKETS_TYPES.VEHICLE_COMPONENT,
    TICKETS_TYPES.EE,
    TICKETS_TYPES.SWM,
  ];
  const initialTicketType = ticketTypes[tabFromUrl] || TICKETS_TYPES.VEHICLE_SYSTEM;
  const selectedProject = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariant = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );
  const [selectedTicketType, setSelectedTicketType] = useState(initialTicketType);

  const [ticketDetails, setTicketDetails] = useState([]);
  const [selectedIntersectionIds, setSelectedIntersectionIds] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedVarientId, setSelectedVarientId] = useState(null);
  const [page] = useState(1);
  const [rowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [filterData, setFilterData] = useState({ archive_status: "ACTIVE" });
  const [activeIntrId, setActiveIntrId] = useState(intrIdFromUrl);

  useEffect(() => {
    const hydrateSelectionFromUrl = async () => {
      if (
        hydratedSelectionRef.current ||
        !projectIdFromUrl ||
        !variantIdFromUrl
      ) {
        return;
      }

      hydratedSelectionRef.current = true;

      await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...projectConfigAPI.get_projects_main,
            params: {
              page_number: 1,
              page_size: 10000,
              order_by_column: "created_at",
              order_by_sort: "DESC",
            },
            show_toast: false,
          }),
        ),
      );
      dispatch(selectProjectAction(Number(projectIdFromUrl)));

      await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...projectConfigAPI.get_variants_main,
            params: {
              project_id: Number(projectIdFromUrl),
              is_complete_details_required: true,
              sort_column: "name",
              sort_order: "ASC",
            },
            show_toast: false,
          }),
        ),
      );
      dispatch(selectVariantAction(Number(variantIdFromUrl)));
    };

    hydrateSelectionFromUrl();
  }, [dispatch, projectIdFromUrl, variantIdFromUrl]);

  const memoizedProjectVariant = useMemo(
    () => ({
      projectId: projectIdFromUrl || selectedProject?.project_id,
      variantId: variantIdFromUrl || selectedVariant?.variant_id,
    }),
    [
      projectIdFromUrl,
      selectedProject?.project_id,
      selectedVariant?.variant_id,
      variantIdFromUrl,
    ],
  );

  const fetchTickets = useCallback(
    async (requestObj = {}) => {
      if (selectedTicketType) {
        const resp = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...typeApprovalAPI?.get_tickets,
              params: {
                project_id: memoizedProjectVariant.projectId,
                variant_id: memoizedProjectVariant.variantId,
                page_size: rowsPerPage,
                page_number: page,
                status: "100,101,150,200,300,400,500,600,999",
                tickets_type: selectedTicketType,
                ...(activeIntrId && { intr_id: activeIntrId }),
                archive_status: "ACTIVE",
                ...filterData,
                ...requestObj,
              },
              show_toast: false,
            }),
          ),
        );
        if (resp.error) {
          setTicketDetails([]);
        } else {
          setTicketDetails(resp.data);
          setTotalCount(resp?.total_count);
        }
      }
    },
    [
      dispatch,
      memoizedProjectVariant.projectId,
      memoizedProjectVariant.variantId,
      rowsPerPage,
      page,
      selectedTicketType,
      filterData,
      activeIntrId,
    ],
  );

  const handleReCreateTicket = async () => {
    const payload = {
      intersection_ids: selectedIntersectionIds,
      current_time: moment().format(),
    };
    const response = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...typeApprovalAPI?.re_create_tickets,
          params: payload,
          show_toast: true,
          onSuccess: (response) => {
            fetchTickets();
            setSelectedIntersectionIds([]);
          },
          onError: (error) => {
            setSelectedIntersectionIds([]);
          },
        }),
      ),
    );
    if (!response.error) {
      fetchTickets();
    }
    setSelectedIntersectionIds([]);
  };

  const handleDeleteDraftTicket = async () => {
    const payload = {
      intersection_ids: selectedIntersectionIds,
      project_id: Number.parseInt(selectedProjectId, 10),
      variant_id: Number(selectedVarientId),
    };
    const response = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...typeApprovalAPI?.delete_tickets,
          params: payload,
          show_toast: true,
        }),
      ),
    );
    if (!response.error) {
      fetchTickets();
    }
    setSelectedIntersectionIds([]);
  };

  useEffect(() => {
    setSelectedProjectId(memoizedProjectVariant.projectId);
    setSelectedVarientId(memoizedProjectVariant.variantId);
    if (memoizedProjectVariant.projectId && memoizedProjectVariant.variantId) {
      fetchTickets();
    }
  }, [memoizedProjectVariant, fetchTickets]);

  const handleTabChange = (tabIndex) => {
    setSelectedTicketType(ticketTypes[tabIndex]);
    setTicketDetails([]);
    setActiveIntrId(null);
    setSearchParams((prevParams) => {
      const nextParams = new URLSearchParams(prevParams);
      nextParams.set("tab", String(tabIndex));
      nextParams.delete("intr_id");
      return nextParams;
    });
  };
  return (
    <Grid container>
      <Grid container>
        <Grid item sm={12} sx={{ p: 1 }}>
          <CommonBreadcrumbs
            items={[
              { label: "Regulatory Compliance" },
              { label: "Intersection" },
            ]}
          />
        </Grid>
      </Grid>

      <Grid container sx={{ height: "100%", alignItems: "center", p: 2 }}>
        <Grid item sm={12}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box>
              <TicketsFilter
                getFilterDetails={(data) => {
                  setFilterData(data);
                }}
              />
            </Box>
            <Box>
              <ProjectvariantSearchbar />
            </Box>

            <ProjectVariantDialog />
          </Box>
        </Grid>
        <CustomTabsV2
          onTabChange={handleTabChange}
          defaultTab={tabFromUrl}
          centerTabs={true}
          renderComponentBelow={
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                pr: 3,
              }}
            >
              <CustomButtonV2
                btnName="Raise Ticket"
                disabled={selectedIntersectionIds.length === 0}
                handleOnClick={() => {
                  handleReCreateTicket();
                }}
              />
              <CustomButtonV2
                btnName="Delete"
                disabled={selectedIntersectionIds.length === 0}
                handleOnClick={() => {
                  handleDeleteDraftTicket();
                }}
              />
            </Box>
          }

          tabs={[
            {
              name: "Vehicle System",
              count: "",
              content: (
                <TicketsTable
                  tickets_type={TICKETS_TYPES.VEHICLE_SYSTEM}
                  ticketDetails={ticketDetails}
                  selectedTicketType={selectedTicketType}
                  setSelectedTicketType={setSelectedTicketType}
                  selectedIntersectionIds={selectedIntersectionIds}
                  setSelectedIntersectionIds={setSelectedIntersectionIds}
                  totalCount={totalCount}
                  fetchTickets={fetchTickets}
                  enableSelect={true}
                  showPagination={true}
                  enableAction={true}
                  parentPage="intersections"
                />
              ),
            },
            {
              name: "Vehicle Component",
              count: "",
              content: (
                <TicketsTable
                  tickets_type={TICKETS_TYPES.VEHICLE_COMPONENT}
                  ticketDetails={ticketDetails}
                  selectedTicketType={selectedTicketType}
                  setSelectedTicketType={setSelectedTicketType}
                  selectedIntersectionIds={selectedIntersectionIds}
                  setSelectedIntersectionIds={setSelectedIntersectionIds}
                  totalCount={totalCount}
                  fetchTickets={fetchTickets}
                  enableSelect={true}
                  showPagination={true}
                  enableAction={true}
                  parentPage="intersections"
                />
              ),
            },
            {
              name: "EE Component",
              count: "",
              content: (
                <TicketsTable
                  tickets_type={TICKETS_TYPES.EE}
                  ticketDetails={ticketDetails}
                  selectedTicketType={selectedTicketType}
                  setSelectedTicketType={setSelectedTicketType}
                  selectedIntersectionIds={selectedIntersectionIds}
                  setSelectedIntersectionIds={setSelectedIntersectionIds}
                  totalCount={totalCount}
                  fetchTickets={fetchTickets}
                  enableSelect={true}
                  showPagination={true}
                  enableAction={true}
                  parentPage="intersections"
                />
              ),
            },
            {
              name: "Software Module",
              count: "",
              content: (
                <TicketsTable
                  tickets_type={TICKETS_TYPES.SWM}
                  ticketDetails={ticketDetails}
                  selectedTicketType={selectedTicketType}
                  setSelectedTicketType={setSelectedTicketType}
                  selectedIntersectionIds={selectedIntersectionIds}
                  setSelectedIntersectionIds={setSelectedIntersectionIds}
                  totalCount={totalCount}
                  fetchTickets={fetchTickets}
                  enableSelect={true}
                  showPagination={true}
                  enableAction={true}
                  parentPage="intersections"
                />
              ),
            },
          ]}
        />
      </Grid>
    </Grid>
  );
}

export default IntersectionIndex;
